import { produce } from "immer";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { parseJwt } from "@libs/utils/helpers/parseJwt";
import { addCookie, removeCookie } from "@libs/utils/helpers/getSafeCookies";

import constants from "@libs/acp/common/constants";

import type { UserStore, UserACP, AccessToken } from "./types";

import { userACPInitialState } from "./initialState";
import { jwtDecode } from "jwt-decode";

const setACPSession = (accessToken: string) => {
	const { exp } = parseJwt(accessToken);
	const expires = new Date(exp * 1000);

	addCookie({
		key: constants.cookies.ACP_SESSION,
		value: accessToken,
		encrypt: true,
		expires,
	});
};

const setCachedSessionForLPs = (refreshToken: string) => {
	const { exp } = parseJwt(refreshToken);

	const expFromTokenToDateFormat = new Date(exp * 1000);

	addCookie({
		key: "user_logged_in",
		value: "true",
		expires: expFromTokenToDateFormat,
	});
};

export const useUserStore = create<UserStore>()(
	devtools(
		persist(
			(set, get) => {
				const setState = (fn: (state: UserStore) => void) => set(produce(fn));

				return {
					authenticatedUser: {
						acp: userACPInitialState,
					},
					addAuthenticatedUser: (payload, app) => {
						set({
							authenticatedUser: {
								...get().authenticatedUser,
								[app]: {
									...payload,
									customer: {
										...payload.customer,
										contactStatus: {
											...userACPInitialState?.customer?.contactStatus,
										},
									},
								},
							},
						});

						if (app === "acp") {
							const acpPayload = payload as UserACP;
							const refreshToken = acpPayload.auth.refresh_token;

							if (refreshToken) {
								setCachedSessionForLPs(refreshToken);
								setACPSession(acpPayload.auth.access_token);
							}
						}
					},
					updateACPAuthSession: (payload) => {
						setState((state) => {
							state.authenticatedUser.acp.auth = payload;
						});

						const refreshToken = payload.refresh_token;
						if (refreshToken) {
							setCachedSessionForLPs(refreshToken);
							setACPSession(payload.access_token);
						}
					},
					updateACPUSerContactStatus: (contactType, payload) => {
						setState((state) => {
							state.authenticatedUser.acp.customer.contactStatus[contactType] =
								payload;
						});
					},
					updateACPCustomer: (patch) => {
						setState((state) => {
							Object.assign(state.authenticatedUser.acp.customer, patch);
						});
					},
					resetStore: () => {
						useUserStore.persist.clearStorage();
						set({
							authenticatedUser: {
								acp: userACPInitialState,
							},
						});
					},
					resetACP: () => {
						sessionStorage.removeItem(constants.acp.FIRST_AUTHENTICATION_TYPE);
						removeCookie("user_logged_in");

						setState((state) => {
							state.authenticatedUser.acp = userACPInitialState;
						});
					},
					removeRequiredUpdateItem: (item) => {
						setState((state) => {
							state.authenticatedUser.acp.customer.required_updates =
								state.authenticatedUser.acp.customer.required_updates?.filter(
									(i) => i !== item,
								);
						});
					},
					hasPermission: (permissionName) => {
						const accessToken =
							get().authenticatedUser?.acp?.auth?.access_token;

						if (!accessToken) return false;

						const decodedAccessToken =
							accessToken && (jwtDecode(accessToken) as AccessToken);

						const permissions = decodedAccessToken
							? decodedAccessToken?.permissions
							: [];
						if (!permissions?.length) return false;

						return permissions.some(
							(permission) => permission.name === permissionName,
						);
					},
					containsPermissionGroup: (permissionGroup) => {
						const accessToken =
							get().authenticatedUser?.acp?.auth?.access_token;

						const decodedAccessToken =
							accessToken && (jwtDecode(accessToken) as AccessToken);

						const permissions = decodedAccessToken
							? decodedAccessToken?.permissions
							: [];

						if (!permissions?.length) return false;

						const flatPermissions = permissions.map(
							(permissions) => permissions.name,
						);

						return flatPermissions.some((permissionName) =>
							permissionName.includes(permissionGroup),
						);
					},
					tokenOrigin: () => {
						const accessToken =
							get().authenticatedUser?.acp?.auth?.access_token;

						const decodedAccessToken: AccessToken = accessToken
							? jwtDecode(accessToken)
							: ({} as AccessToken);

						const origin = decodedAccessToken.origin || "";

						return origin;
					},
				};
			},
			{
				name: "acp-user-store",
			},
		),
	),
);
